"use client"

import { useEffect, useState } from "react"
import Image from "next/image"
import { Modal } from "./modal"
import styles from "./marketing-modal.module.css"
import { handleOfferClick } from "@/components/OfferPage/OfferList";
import type { OfferModalObj } from '@/types/modals'
import type { Site } from "@/types/sites";
import {
    ClickoutLocation,
    OfferPageTemplateResponse,
    SanitisedOffer,
} from "@/types/offers";
import { OfferBlockWithForm } from "@/components/OfferPage/OfferBlockWithForm";
import { getGAAttributes } from "@/utils/gaUtils";
import { logNewGoogleAnalyticsEvent } from "src/utils/utils";

const OfferModal = ({
    modalProps,
    site,
    offerPage,
}: {
    modalProps: OfferModalObj;
    site: Site;
    offerPage: OfferPageTemplateResponse;
}) => {
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    const openMarketingModal = () => {
      if (modalProps?.offers?.length) {
      setIsOpen(true)
        logNewGoogleAnalyticsEvent(
          "modal-opened",
          {
            title: modalProps.title,
            subtitle: modalProps.subtitle,
          },
          offerPage?.data?.attributes?.offerSlug
        )
      } else {
        console.log('No offers found in modalProps')
      }
    }

    window.openMarketingModal = () => {
      openMarketingModal();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Modal open={isOpen} onOpenChange={setIsOpen} design={modalProps.design}>
      <div className={`${styles.modalContainer} ${modalProps.design === 'modal' ? styles.modal : styles.overlay}`}>
        <div className={styles.modalHeader}>
          <h2 className={styles.title}>{modalProps.title}</h2>
          <p className={styles.subtitle}>{modalProps.subtitle}</p>
        </div>
        <div className={styles.offerGrid}>
          {modalProps.offers.map((offer, index) => 
            modalProps.hasPresentation ? (
              <OfferBlockWithForm
                key={offer.id}
                offer={offer}
                useMerchantColours={offerPage?.data?.attributes?.useMerchantColours}
                index={index + 1}
                offerPage={offerPage}
                site={site}
                epcId={offer.epcId ?? ''}
                location={ClickoutLocation.Modal}
              />
            ) : (
              <OfferRow
                key={offer.id}
                offer={offer}
                page={offerPage}
                site={site}
                index={index + 1}
              />
            )
          )}
        </div>
        {modalProps.modalStyles && modalProps.modalStyles && (
          <style>
            {modalProps.modalStyles}
          </style>
        )}
        <button className={styles.closeModalButton} onClick={() => setIsOpen(false)}>
          Close
        </button>
      </div>
    </Modal>
  )
};

const OfferRow = ({ 
    offer,
    page, 
    site,
    index
}: {
    offer: SanitisedOffer;
    page: OfferPageTemplateResponse;
    site: Site;
    index: number
}) => {
    const gaAttributes = getGAAttributes(offer, index);

    return (
      offer.logos?.primary &&
        <form className={styles.offerContainer}>
            <button onClick={(e) => {
                handleOfferClick(e, offer, index, page, site, offer.epcId ?? '', gaAttributes, ClickoutLocation.Modal)
            }} className={styles.gridButton}
            aria-label={`Go to ${offer.merchant.data?.attributes.name} offers`}>
            <div className={styles.imageContainer}>
                <Image
                    className={styles.merchantImage}
                    src={ offer.logos.primary.src}
                    alt={'Go to ' + offer.merchant.data?.attributes.name + '\'s offers!'}
                    width={offer?.logos?.primary?.width}
                    height={offer?.logos?.primary?.height}
                    loading="lazy"
                    style={{
                      objectFit: "contain",
                      maxWidth: "100%",
                      maxHeight: "80%",
                  }}
                />
            </div>
            <div>
                <div className={styles.offerTitle}>{offer?.title}</div>
                <div className={styles.subTitle}>{offer?.tagline ?? ''}</div>
            </div>
            <div>
                <div className="playButton">
                    PLAY
                </div>
                <div className={styles.visitMerchant}>
                    Visit {offer.merchant.data?.attributes.name}
                </div>
            </div>
        </button>
        <div
          className={styles.termsAndConditions}
          dangerouslySetInnerHTML={{
              __html: offer.termsAndConditionsHTML ?? '',
          }}
          ></div>
    </form>
    )
}

export default OfferModal;